/**
 * Composable d'upload de fichiers pour les custom fields d'adhésion (/a/[slug]).
 * Extrait du composant index.vue pour réduire la taille.
 *
 * État par participant + fieldId encodé en `{participantIndex}-{fieldId}`.
 * Validations : taille (10 Mo max) + types autorisés (PDF/JPG/PNG/WebP).
 */

interface UploadResponse {
  success: boolean;
  data?: { file_id: string; file_name: string };
  message?: string;
}

type ParticipantWithCustomFields = {
  customFieldValues: Record<string, unknown>;
};

export function useMembershipFileUpload(
  participants: Ref<ParticipantWithCustomFields[]>,
  apiBaseUrl: string,
) {
  const { t } = useI18n();
  const fileUploadingState = ref<Record<string, boolean>>({});
  const fileUploadErrors = ref<Record<string, string>>({});

  function getFileUploadKey(participantIndex: number, fieldId: string): string {
    return `${participantIndex}-${fieldId}`;
  }

  async function uploadCustomFieldFile(
    file: File,
    fieldId: string,
    participantIndex: number,
  ) {
    const key = getFileUploadKey(participantIndex, fieldId);
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedExtensions = [".pdf", ".jpg", ".jpeg", ".png", ".webp"];
    const allowedMimes = [
      "application/pdf",
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
    ];

    // Validation taille
    if (file.size > maxSize) {
      fileUploadErrors.value[key] = t("adhesion.useMembershipFileUpload.fichierTropVolumineux");
      return;
    }

    // Validation type
    const ext = "." + file.name.split(".").pop()?.toLowerCase();
    if (!allowedMimes.includes(file.type) && !allowedExtensions.includes(ext)) {
      fileUploadErrors.value[key] = t("adhesion.useMembershipFileUpload.typeNonAutorise");
      return;
    }

    // Reason: fileUploadErrors is a Record<string, string> indexed by computed key
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete fileUploadErrors.value[key];

    fileUploadingState.value[key] = true;
    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await $fetch<UploadResponse>(
        `${apiBaseUrl}/api/public/membership-files/upload`,
        {
          method: "POST",
          body: formData,
        },
      );

      if (res.success && res.data) {
        const participant = participants.value[participantIndex];
        if (participant) {
          participant.customFieldValues[fieldId] = {
            file_id: res.data.file_id,
            file_name: res.data.file_name,
          };
        }
      } else {
        fileUploadErrors.value[key] = res.message || t("adhesion.useMembershipFileUpload.erreurUpload");
      }
    } catch (e: unknown) {
      const err = e as { data?: { message?: string }; message?: string };
      fileUploadErrors.value[key] =
        err?.data?.message || err?.message || t("adhesion.useMembershipFileUpload.erreurUploadFichier");
    } finally {
      fileUploadingState.value[key] = false;
    }
  }

  function removeUploadedFile(fieldId: string, participantIndex: number) {
    const key = getFileUploadKey(participantIndex, fieldId);
    const participant = participants.value[participantIndex];
    if (participant) {
      // Reason: customFieldValues is a Record<string, unknown> indexed by dynamic field id
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete participant.customFieldValues[fieldId];
    }
    // Reason: fileUploadErrors is a Record<string, string> indexed by computed key
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete fileUploadErrors.value[key];
  }

  function isFileUploaded(fieldId: string, participantIndex: number): boolean {
    const participant = participants.value[participantIndex];
    if (!participant) return false;
    const val = participant.customFieldValues[fieldId];
    return !!val && typeof val === "object" && "file_id" in val;
  }

  function isFileUploading(fieldId: string, participantIndex: number): boolean {
    const key = getFileUploadKey(participantIndex, fieldId);
    return !!fileUploadingState.value[key];
  }

  function getFileUploadError(fieldId: string, participantIndex: number): string | undefined {
    const key = getFileUploadKey(participantIndex, fieldId);
    return fileUploadErrors.value[key];
  }

  const isAnyFileUploading = computed(() => {
    return Object.values(fileUploadingState.value).some(Boolean);
  });

  return {
    uploadCustomFieldFile,
    removeUploadedFile,
    isFileUploaded,
    isFileUploading,
    getFileUploadError,
    isAnyFileUploading,
  };
}
